import { call, put, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../config/app';
import { endpoints } from '../../constants/routing';
import { IResponse } from '../../types/http';
import { IReduxAction } from '../../types/redux';
import { IApiError } from '../../types/shell';
import { IEngagementOrderIdRequestActionPayload, IEngagementOrderIdResponseActionPayload } from '../../types/tools';
import { buildEndpointRoute } from '../../utils/routing';
import * as ToolsActions from '../actions/tools';
import { AuthRequest } from './helpers';

export function* fetchEngagementByOrderIdWorkerSaga(
  action: IReduxAction<IEngagementOrderIdRequestActionPayload>,
): Generator<any, any, any> {
  const { orderId, resolve, reject, type } = action.payload!;

  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.report, { id: orderId })}`;

    const response: IResponse<Omit<IEngagementOrderIdResponseActionPayload, 'type'> | IApiError> = yield call(
      AuthRequest,
      {
        endpoint,
        method: 'GET',
      },
    );
    if (!response.ok) {
      if (reject) {
        reject({ ...response.body, status: response.status });
      }
      yield put(ToolsActions.fetchEngagementByOrderIdFailure(response.body as IApiError));
    } else {
      if (resolve) {
        resolve(response.body);
      }
      yield put(
        ToolsActions.fetchEngagementByOrderIdSuccess({
          type,
          ...response.body,
        } as IEngagementOrderIdResponseActionPayload),
      );
    }
  } catch (error) {
    if (reject) {
      reject(error);
    }
    yield put(ToolsActions.fetchEngagementByOrderIdFailure(error));
  }
}

export function* deleteEngagementByOrderIdWorkerSaga(
  action: IReduxAction<IEngagementOrderIdRequestActionPayload>,
): Generator<any, any, any> {
  const { orderId, resolve, reject, type } = action.payload!;

  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.deleteEngagement, { id: orderId })}`;

    const response = yield call<any>(AuthRequest, {
      endpoint,
      method: 'DELETE',
    });
    if (!response.ok) {
      if (reject) {
        reject(response.body);
      }
      yield put(ToolsActions.deleteEngagementByOrderIdFailure(response.body));
    } else {
      if (resolve) {
        resolve(response.body);
      }
      yield put(ToolsActions.deleteEngagementByOrderIdSuccess({ orderId, type }));
    }
  } catch (error) {
    if (reject) {
      reject(error);
    }
    yield put(ToolsActions.deleteEngagementByOrderIdFailure(error));
  }
}

export function* cancelEngagementByOrderIdWorkerSaga(
  action: IReduxAction<IEngagementOrderIdRequestActionPayload>,
): Generator<any, any, any> {
  const { orderId, resolve, reject, type } = action.payload!;

  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.cancelEngagement, { id: orderId })}`;

    const response = yield call<any>(AuthRequest, {
      endpoint,
      method: 'PATCH',
    });
    if (!response.ok) {
      if (reject) {
        reject(response.body);
      }
      yield put(ToolsActions.cancelEngagementByOrderIdFailure(response.body));
    } else {
      if (resolve) {
        resolve(response.body);
      }
      yield put(ToolsActions.cancelEngagementByOrderIdSuccess({ orderId, type }));
    }
  } catch (error) {
    if (reject) {
      reject(error);
    }
    yield put(ToolsActions.cancelEngagementByOrderIdFailure(error));
  }
}

const sagas = {
  *watchFetchEngagementByOrderIdRequest() {
    yield takeLatest(ToolsActions.FETCH_ENGAGEMENT_BY_ORDER_ID_REQUEST, fetchEngagementByOrderIdWorkerSaga);
  },
  *watchDeleteEngagementByOrderIdRequest() {
    yield takeLatest(ToolsActions.DELETE_ENGAGEMENT_BY_ORDER_ID_REQUEST, deleteEngagementByOrderIdWorkerSaga);
  },
  *watchCancelEngagementByOrderIdRequest() {
    yield takeLatest(ToolsActions.CANCEL_ENGAGEMENT_BY_ORDER_ID_REQUEST, cancelEngagementByOrderIdWorkerSaga);
  },
};

export default sagas;
